import { useCallback, useEffect } from 'react';
import { getIn, useFormikContext } from 'formik';

import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';

import { EMPTY_ARRAY } from '@e-socna/empty';
import type { AddressTypeId } from '@e-socna/data-layer';

import { CheckboxList } from '../../../../components/CheckboxList';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFormikOnNextTick } from '../../../../hooks/useFormikOnNextTick.hook';
import type { ReferencesContext_AddressableConfig } from '../../../../contexts/ReferencesContext';
import { IdMultiSelect } from '../../../../components/IdSelect';
import type { IdOption } from '../../../../components/IdSelect';

import { isFormEmpty } from '../../../utils/is-form-empty.util';

import { AddressFormTab } from '../../enums/tab.enum';
import type { AddressForm } from '../../types/address-form.type';

interface Props {
  readonly config: ReferencesContext_AddressableConfig;
  readonly id: string;
  readonly setEnabledSteps: (enabledSteps: readonly AddressFormTab[]) => void;
}

export const AddressGeneralInfoFormStep = ({
  config,
  setEnabledSteps,
}: Readonly<Props>) => {
  const {
    errors,
    handleBlur,
    setFieldTouched,
    setFieldValue,
    touched,
    values,
  } = useFormikContext<AddressForm>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  useEffect(() => {
    const enabledSteps: AddressFormTab[] = [
      ...(!isFormEmpty({ node: values.postalInfo })
        ? [AddressFormTab.PostalInfo]
        : EMPTY_ARRAY),
      ...(!isFormEmpty({
        node: values.locationInfo,
        ignore: ['_id', '__typename', 'projectionId'],
      })
        ? [AddressFormTab.LocationInfo]
        : EMPTY_ARRAY),
      ...(!isFormEmpty({ node: values.cadastralInfo })
        ? [AddressFormTab.CadastralInfo]
        : EMPTY_ARRAY),
    ];

    setFieldValueOnNextTick('enabledSteps', enabledSteps);
    setEnabledSteps(enabledSteps);
  }, [
    setEnabledSteps,
    setFieldValueOnNextTick,
    values.cadastralInfo,
    values.locationInfo,
    values.postalInfo,
  ]);

  const { addressTypes } = useReferencesContext();

  const relevantAddressTypes = addressTypes.filter((addressType) =>
    config.allowedTypeIds.includes(addressType._id),
  );

  const handleEnabledStepsChange = useCallback(
    (newEnabledSteps: readonly AddressFormTab[]) => {
      void setFieldValue('enabledSteps', newEnabledSteps);
      void setFieldTouched('enabledSteps', true, false);
      setEnabledSteps(newEnabledSteps);
    },
    [setEnabledSteps, setFieldTouched, setFieldValue],
  );

  const handleTypeIdsChange = useCallback(
    (ids: readonly AddressTypeId[]) => {
      void setFieldValue('typeIds', ids);
    },
    [setFieldValue],
  );

  return (
    <Grid container spacing={2} sx={{ alignContent: 'flex-start' }}>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <IdMultiSelect<AddressTypeId, IdOption<AddressTypeId>>
          disabled={relevantAddressTypes.length === 1}
          error={!!(errors.typeIds && touched.typeIds)}
          helperText={
            !!(errors.typeIds && touched.typeIds) &&
            (getIn(errors, 'typeIds') as string)
          }
          id="address-general-info-type-ids"
          label="Types de l'adresse"
          name="typeIds"
          onBlur={handleBlur}
          onChange={handleTypeIdsChange}
          options={relevantAddressTypes.map((addressType) => ({
            id: addressType._id,
            label: addressType.label,
          }))}
          required
          value={values.typeIds as readonly AddressTypeId[]}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <FormControl
          component="fieldset"
          error={
            !!(getIn(errors, 'enabledSteps') && getIn(touched, 'enabledSteps'))
          }
          required
          sx={{ m: 3 }}
          variant="standard"
        >
          <FormLabel component="legend">
            Choisissez les informations liées à l&apos;addresse
          </FormLabel>
          {/* A step holding data cannot be disabled. */}
          <CheckboxList<AddressFormTab>
            idPrefix="address-enabled-steps-"
            items={[
              {
                disabled: !isFormEmpty({ node: values.postalInfo }),
                id: AddressFormTab.PostalInfo,
                label: 'Informations Postales',
              },
              {
                disabled: !isFormEmpty({
                  node: values.locationInfo,
                  ignore: ['_id', '__typename', 'projectionId'],
                }),
                id: AddressFormTab.LocationInfo,
                label: 'Informations de Localisation',
              },
              {
                disabled: !isFormEmpty({ node: values.cadastralInfo }),
                id: AddressFormTab.CadastralInfo,
                label: 'Informations Cadastrales',
              },
            ]}
            onChange={handleEnabledStepsChange}
            value={values.enabledSteps}
            variant="edition"
          />
          {!!(
            getIn(errors, 'enabledSteps') && getIn(touched, 'enabledSteps')
          ) && <FormHelperText>{getIn(errors, 'enabledSteps')}</FormHelperText>}
        </FormControl>
      </Grid>
    </Grid>
  );
};
